<template>
    <Modal2
        v-if="visible"
        :title="$t('auto.messageFilterConfigurationTitle')"
        :ok-text="okText || $t('message.save')"
        :cancel-text="cancelText || $t('message.cancelText')"
        :show-footer="showFooter"
        @ok="handleSave"
        @cancel="handleCancel"
        large
    >
        <div class="message-matched-config-form">
            <div class="filters-container">
                <!-- Author Filter -->
                <div class="filter-row">
                    <span class="filter-text">
                        <span class="prefix">{{ $t('auto.messageMatchedConfigGNderen') }}</span>
                        <InlineSelectOperator
                            v-model="localConfig.authorOperator"
                            :options="[{ label: 'eşittir', value: 'equal' }]"
                        />
                        <InlineSelectValue
                            v-model="localConfig.author"
                            :options="authorOptions"
                            placeholder="seçin"
                        />
                    </span>
                </div>

                <!-- Message Type Filter -->
                <div class="filter-row">
                    <span class="filter-text">
                        <span class="prefix">{{ $t('auto.messageMatchedConfigMesajTipi') }}</span>
                        <InlineSelectOperator
                            v-model="localConfig.messageTypeOperator"
                            :options="[{ label: 'eşittir', value: 'equal' }]"
                        />
                        <InlineSelectValue
                            v-model="localConfig.messageType"
                            :options="messageTypeOptions"
                            placeholder="seçin"
                        />
                    </span>
                </div>

                <!-- Platform Filter -->
                <div class="filter-row">
                    <span class="filter-text">
                        <span class="prefix">{{ $t('auto.messageMatchedConfigPlatform') }}</span>
                        <InlineSelectOperator
                            v-model="localConfig.platformOperator"
                            :options="[{ label: 'eşittir', value: 'equal' }]"
                        />
                        <InlineSelectValue
                            v-model="localConfig.platform"
                            :options="platformOptions"
                            placeholder="seçin"
                        />
                    </span>
                </div>

                <!-- Channels Filter -->
                <div class="filter-row">
                    <span class="filter-text">
                        <span class="prefix">{{ $t('auto.messageMatchedConfigKanallar') }}</span>
                        <InlineSelectOperator
                            v-model="localConfig.channelsOperator"
                            :options="[
                                { label: 'içerir', value: 'contain' },
                                { label: 'tümü', value: 'all' }
                            ]"
                        />
                        <span v-if="localConfig.channelsOperator === 'contain'" class="multi-value-wrapper">
                            <a-select
                                v-model="localConfig.channels"
                                mode="multiple"
                                class="multi-select-inline"
                                :placeholder="$t('auto.messageMatchedConfigKanalSeIn')"
                            >
                                <a-select-option v-for="ch in allChannelsForPicker" :key="ch.value" :value="ch.value">
                                    {{ ch.label }}
                                </a-select-option>
                            </a-select>
                        </span>
                        <span v-else class="all-channels-text">{{ $t('auto.messageMatchedConfigTMKanallar') }}</span>
                    </span>
                </div>

                <!-- Text Filter -->
                <div class="filter-row">
                    <span class="filter-text">
                        <span class="prefix">{{ $t('auto.messageMatchedConfigMesajMetni') }}</span>
                        <InlineSelectOperator
                            v-model="localConfig.textFilter.type"
                            :options="textFilterTypeOptions"
                        />
                        <InlineInput
                            v-if="!['empty', 'not_empty'].includes(localConfig.textFilter.type)"
                            v-model="localConfig.textFilter.value"
                            type="text"
                            placeholder="metin girin"
                        />
                    </span>
                </div>

                <!-- Case Sensitive -->
                <div class="filter-row" v-if="!['empty', 'not_empty'].includes(localConfig.textFilter.type)">
                    <span class="filter-text">
                        <a-checkbox v-model="localConfig.textFilter.caseSensitive">
                            {{ $t('auto.messageMatchedConfigBYKKKHarfDuyarl') }}
                        </a-checkbox>
                    </span>
                </div>
            </div>

            <div class="config-info">
                <i class="material-icons">info</i>
                <span>{{ $t('auto.messageMatchedConfigBuFiltrelereUyanMesajlarGeldiIndeOtomasyonTetiklenecektir') }}</span>
            </div>

            <div v-if="!showFooter" class="form-actions d-flex gap-5 flex-end" style="margin-top: 20px">
                <SupsisButton @click="handleCancel">{{ cancelText || $t("message.cancelText") }}</SupsisButton>
                <SupsisButton primary @click="handleSave">{{ okText || $t("message.save") }}</SupsisButton>
            </div>
        </div>
    </Modal2>
</template>

<script>
import Vue from "vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import InlineSelectOperator from "./InlineSelectOperator.vue";
import InlineSelectValue from "./InlineSelectValue.vue";
import InlineInput from "./InlineInput.vue";

export default Vue.extend({
    name: "MessageMatchedConfig",
    components: {
        Modal2,
        SupsisButton,
        InlineSelectOperator,
        InlineSelectValue,
        InlineInput,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        value: {
            type: Object,
            default: () => ({
                author: "all",
                messageType: "all",
                platform: "all",
                channels: [],
                textFilter: {
                    type: "contains",
                    value: "",
                    caseSensitive: false,
                },
            }),
        },
        okText: {
            type: String,
            default: "",
        },
        cancelText: {
            type: String,
            default: "",
        },
        showFooter: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            localConfig: {
                author: "all",
                authorOperator: "equal",
                messageType: "all",
                messageTypeOperator: "equal",
                platform: "all",
                platformOperator: "equal",
                channels: [],
                channelsOperator: "all",
                textFilter: {
                    type: "contains",
                    value: "",
                    caseSensitive: false,
                },
            },
        };
    },
    computed: {
        channels() {
            return this.$store.state.channel.channels || [];
        },
        allChannelsForPicker() {
            return this.channels
                .filter((ch) => !ch.isDeleted)
                .map((ch) => ({
                    label: `${ch.name} (${this.getChannelTypeLabel(ch.type)})`,
                    value: ch._id,
                }));
        },
        authorOptions() {
            return [
                { label: "Tümü", value: "all" },
                { label: "Müşteri (Visitor)", value: "customer" },
                { label: "Temsilci", value: "agent" },
                { label: "Chatbot", value: "chatbot" },
            ];
        },
        messageTypeOptions() {
            return [
                { label: "Tümü", value: "all" },
                { label: "Metin", value: "text" },
                { label: "Tüm Medyalar", value: "media" },
                { label: "Resim", value: "image" },
                { label: "Video", value: "video" },
                { label: "Ses", value: "audio" },
                { label: "Dosya", value: "file" },
                { label: "Konum", value: "location" },
                { label: "JSON", value: "json" },
            ];
        },
        platformOptions() {
            return [
                { label: "Tümü", value: "all" },
                { label: "Web", value: "web" },
                { label: "WhatsApp", value: "whatsapp" },
                { label: "Instagram", value: "instagram" },
                { label: "Messenger", value: "messenger" },
                { label: "Telegram", value: "telegram" },
                { label: "LinkedIn", value: "linkedin" },
            ];
        },
        textFilterTypeOptions() {
            return [
                { label: "içerir", value: "contains" },
                { label: "içermez", value: "not_contains" },
                { label: "ile başlar", value: "starts_with" },
                { label: "ile biter", value: "ends_with" },
                { label: "tam eşleşir", value: "exact" },
                { label: "regex ile eşleşir", value: "regex" },
                { label: "boş", value: "empty" },
                { label: "dolu", value: "not_empty" },
            ];
        },
    },
    methods: {
        handleSave() {
            const needsValue = !["empty", "not_empty"].includes(this.localConfig.textFilter.type);
            if (needsValue && !this.localConfig.textFilter.value) {
                this.$message.error("Lütfen aranacak metni giriniz");
                return;
            }

            this.$emit("save", this.localConfig);
        },
        handleCancel() {
            this.$emit("cancel");
        },
        getChannelTypeLabel(type) {
            const typeLabels = {
                "whatsapp-business-api": "WhatsApp Business API",
                "whatsapp-business-api-supsis": "WhatsApp Business API",
                "whatsapp-v2": "WhatsApp V2",
                "whatsapp-unipile": "WhatsApp V3",
                instagram: "Instagram",
                messenger: "Messenger",
                "telegram-unipile": "Telegram",
                "linkedin-unipile": "LinkedIn",
                webchat: "Web Chat",
            };
            return typeLabels[type] || type;
        },

        initializeConfig() {
            this.localConfig = {
                author: this.value.author || "all",
                authorOperator: this.value.authorOperator || "equal",
                messageType: this.value.messageType || "all",
                messageTypeOperator: this.value.messageTypeOperator || "equal",
                platform: this.value.platform || "all",
                platformOperator: this.value.platformOperator || "equal",
                channels: this.value.channels || [],
                channelsOperator: this.value.channelsOperator || (this.value.channels && this.value.channels.length > 0 ? "contain" : "all"),
                textFilter: {
                    type: (this.value.textFilter && this.value.textFilter.type) || "contains",
                    value: (this.value.textFilter && this.value.textFilter.value) || "",
                    caseSensitive: (this.value.textFilter && this.value.textFilter.caseSensitive) || false,
                },
            };
        },
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.initializeConfig();
            }
        },
        value: {
            handler(newVal) {
                if (this.visible && newVal) {
                    this.initializeConfig();
                }
            },
            deep: true,
        },
    },
    mounted() {
        this.initializeConfig();
    },
});
</script>

<style lang="scss" scoped>
.message-matched-config-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 10px 0;

    .filters-container {
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

        .filter-row {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 12px;
            font-size: 15px;
            line-height: 32px;

            .filter-text {
                display: flex;
                align-items: center;
                gap: 6px;
                flex: 1;
                flex-wrap: wrap;

                .prefix {
                    color: #666;
                    font-weight: 500;
                    margin-right: 4px;
                }

                .multi-value-wrapper {
                    display: inline-flex;
                    align-items: center;
                }

                .all-channels-text {
                    color: #666;
                    font-style: italic;
                    padding: 0 8px;
                }
            }
        }
    }

    .config-info {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px;
        background: #e6f7ff;
        border-radius: 6px;
        color: #1890ff;
        font-size: 13px;
        margin-top: 10px;

        .material-icons {
            font-size: 20px;
        }
    }
}
</style>

<style lang="scss">
.message-matched-config-form {
    .multi-select-inline {
        min-width: 200px !important;

        .ant-select-selection {
            border: none !important;
            border-bottom: 2px solid #52c41a !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: transparent !important;

            .ant-select-selection__rendered {
                margin: 0 !important;
                padding: 2px 8px !important;
                line-height: 28px !important;
            }

            .ant-select-selection__choice {
                background: #f6ffed !important;
                border: 1px solid #95de64 !important;
                color: #52c41a !important;
                font-weight: 500;
            }

            &:hover {
                background: #f6ffed !important;
            }
        }

        &.ant-select-focused .ant-select-selection {
            border-bottom-color: #389e0d !important;
            box-shadow: 0 2px 4px rgba(82, 196, 26, 0.2) !important;
        }
    }
}
</style>
